/**
 * Push Notification Provider
 * ブラウザプッシュ通知の管理コンポーネント
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { message } from 'antd';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Context
const PushNotificationContext = createContext(null);

// Helper: URLセーフBase64をUint8Arrayに変換
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Provider Component
export const PushNotificationProvider = ({ children }) => {
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [registration, setRegistration] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // 初期化
  useEffect(() => {
    // プッシュ通知のサポートチェック
    const supported = 
      'serviceWorker' in navigator && 
      'PushManager' in window && 
      'Notification' in window;
    
    setIsSupported(supported);
    
    if (supported) {
      setPermission(Notification.permission);
      
      // Service Workerの登録
      registerServiceWorker();
    }
  }, []);

  // Service Worker登録
  const registerServiceWorker = useCallback(async () => {
    try {
      const reg = await navigator.serviceWorker.register('/service-worker.js');
      setRegistration(reg);
      console.log('[Push] Service Worker registered:', reg);
      
      // 既存のサブスクリプションをチェック
      const existingSubscription = await reg.pushManager.getSubscription();
      if (existingSubscription) {
        setSubscription(existingSubscription);
        setIsSubscribed(true);
        console.log('[Push] Existing subscription found');
      }
    } catch (error) {
      console.error('[Push] Service Worker registration failed:', error);
    }
  }, []);

  // VAPID公開鍵を取得
  const getVapidPublicKey = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/push/vapid-public-key`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      if (!response.ok) {
        throw new Error('VAPID public key not available');
      }
      
      const data = await response.json();
      return data.public_key;
    } catch (error) {
      console.error('[Push] Failed to get VAPID public key:', error);
      return null;
    }
  }, []);

  // 通知許可をリクエスト
  const requestPermission = useCallback(async () => {
    if (!isSupported) {
      message.warning('このブラウザはプッシュ通知に対応していません');
      return false;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      
      if (result === 'granted') {
        console.log('[Push] Permission granted');
        return true;
      } else if (result === 'denied') {
        message.error('通知がブロックされています。ブラウザの設定から許可してください。');
        return false;
      } else {
        message.info('通知の許可がキャンセルされました');
        return false;
      }
    } catch (error) {
      console.error('[Push] Permission request failed:', error);
      return false;
    }
  }, [isSupported]);

  // サブスクリプションを作成して登録
  const subscribe = useCallback(async () => {
    if (!isSupported || !registration) {
      message.warning('プッシュ通知の準備ができていません');
      return false;
    }

    setIsLoading(true);

    try {
      // 通知許可の確認/リクエスト
      if (Notification.permission !== 'granted') {
        const granted = await requestPermission();
        if (!granted) {
          setIsLoading(false);
          return false;
        }
      }

      // VAPID公開鍵を取得
      const vapidPublicKey = await getVapidPublicKey();
      if (!vapidPublicKey) {
        message.error('プッシュ通知の設定に失敗しました');
        setIsLoading(false);
        return false;
      }

      // プッシュサブスクリプションを作成
      const pushSubscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      console.log('[Push] Subscription created:', pushSubscription);

      // バックエンドに登録
      const token = localStorage.getItem('token');
      const keys = pushSubscription.toJSON().keys;
      
      const response = await fetch(`${API_BASE_URL}/api/push/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          endpoint: pushSubscription.endpoint,
          p256dh_key: keys.p256dh,
          auth_key: keys.auth,
          user_agent: navigator.userAgent,
          device_name: navigator.platform || 'Unknown'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to register subscription');
      }

      setSubscription(pushSubscription);
      setIsSubscribed(true);
      message.success('プッシュ通知を有効にしました');
      setIsLoading(false);
      return true;

    } catch (error) {
      console.error('[Push] Subscription failed:', error);
      message.error('プッシュ通知の登録に失敗しました');
      setIsLoading(false);
      return false;
    }
  }, [isSupported, registration, requestPermission, getVapidPublicKey]);

  // サブスクリプションを解除
  const unsubscribe = useCallback(async () => {
    if (!subscription) {
      return true;
    }

    setIsLoading(true);

    try {
      // バックエンドから削除
      const token = localStorage.getItem('token');
      await fetch(`${API_BASE_URL}/api/push/unsubscribe?endpoint=${encodeURIComponent(subscription.endpoint)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // ブラウザのサブスクリプションを解除
      await subscription.unsubscribe();

      setSubscription(null);
      setIsSubscribed(false);
      message.success('プッシュ通知を無効にしました');
      setIsLoading(false);
      return true;

    } catch (error) {
      console.error('[Push] Unsubscription failed:', error);
      message.error('プッシュ通知の解除に失敗しました');
      setIsLoading(false);
      return false;
    }
  }, [subscription]);

  const contextValue = {
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    subscribe,
    unsubscribe,
    requestPermission
  };

  return (
    <PushNotificationContext.Provider value={contextValue}>
      {children}
    </PushNotificationContext.Provider>
  );
};

// Hook
export const usePushNotification = () => {
  const context = useContext(PushNotificationContext);
  if (!context) {
    throw new Error('usePushNotification must be used within a PushNotificationProvider');
  }
  return context;
};

export default PushNotificationProvider;
